import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  formatBytes,
  MAX_UPLOAD_BYTES,
} from '@hvac/contracts';
import { Button, cn } from '@hvac/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The drop target.
 *
 * Five ways in, because a shop uses all of them: drag a file, drag a folder,
 * paste a screenshot, browse, or photograph a receipt on a phone. Anything
 * that makes one of those the awkward path pushes work back to paper.
 *
 * Folder drops need the non-standard `webkitGetAsEntry` traversal, it is the
 * only way to read a directory from a drop, and every browser implements it.
 */

export interface UploadDropzoneProps {
  onFiles: (files: readonly File[]) => void;
  disabled?: boolean;
}

export function UploadDropzone({ onFiles, disabled = false }: UploadDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isReadingFolder, setIsReadingFolder] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  // A drag over a child element fires dragleave on the parent; counting
  // enter/leave pairs is the only reliable way to know when it really left.
  const dragDepth = useRef(0);

  const emit = useCallback(
    (files: readonly File[]) => {
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  /**
   * Paste. A screenshot of an emailed invoice is one of the most common ways a
   * document arrives, and without this the only route is save-then-browse.
   */
  useEffect(() => {
    if (disabled) return undefined;

    const onPaste = (event: ClipboardEvent): void => {
      const target = event.target;
      // Never steal a paste from a text field the user is typing in.
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      const files = [...(event.clipboardData?.files ?? [])];
      if (files.length > 0) {
        event.preventDefault();
        emit(files);
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [emit, disabled]);

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      if (disabled) return;

      const entries = [...event.dataTransfer.items]
        .map((item) => (item.kind === 'file' ? item.webkitGetAsEntry() : null))
        .filter((entry): entry is FileSystemEntry => entry !== null);

      if (entries.some((entry) => entry.isDirectory)) {
        setIsReadingFolder(true);
        try {
          const collected: File[] = [];
          for (const entry of entries) {
            await collectEntry(entry, collected);
          }
          emit(collected);
        } finally {
          setIsReadingFolder(false);
        }
        return;
      }

      emit([...event.dataTransfer.files]);
    },
    [emit, disabled],
  );

  return (
    <div
      onDragEnter={(event) => {
        event.preventDefault();
        dragDepth.current += 1;
        if (!disabled) setIsDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setIsDragging(false);
      }}
      onDrop={(event) => void handleDrop(event)}
      className={cn(
        'relative rounded-[var(--radius-surface)] border-2 border-dashed p-6 text-center sm:p-10',
        'transition-all duration-[var(--duration-quick)]',
        isDragging
          ? 'scale-[1.01] border-transparent bg-brand-50 shadow-[var(--shadow-glow)]'
          : 'border-border-strong bg-surface',
        disabled && 'pointer-events-none opacity-60',
      )}
    >
      {/* While a file hovers, the static dashes hand over to marching ants. */}
      {isDragging ? (
        <svg className="pointer-events-none absolute inset-0" aria-hidden="true">
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="15"
            fill="none"
            stroke="var(--color-brand-500)"
            strokeWidth="2"
            className="animate-ants"
          />
        </svg>
      ) : null}
      <p className="text-base font-semibold text-content">
        {isReadingFolder
          ? 'Reading folder…'
          : isDragging
            ? 'Let go, the system takes it from here.'
            : 'Drop receipts and invoices here'}
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-content-muted">
        Drag files or a whole folder, paste a screenshot, or browse. Up to{' '}
        {formatBytes(MAX_UPLOAD_BYTES)} per file.
      </p>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <Button onClick={() => fileInput.current?.click()} disabled={disabled}>
          Browse files
        </Button>
        <Button
          variant="secondary"
          onClick={() => folderInput.current?.click()}
          disabled={disabled}
          className="hidden sm:inline-flex"
        >
          Select a folder
        </Button>
        {/* Only offered where a camera plausibly exists, so a desktop user is
            not shown a control that opens a file picker pretending to be one. */}
        <Button
          variant="secondary"
          onClick={() => cameraInput.current?.click()}
          disabled={disabled}
          className="sm:hidden"
        >
          Take a photo
        </Button>
      </div>

      <p className="mt-4 text-xs text-content-subtle">{ACCEPTED_EXTENSIONS.join(' · ')}</p>

      <input
        ref={fileInput}
        type="file"
        multiple
        accept={ACCEPTED_MIME_TYPES.join(',')}
        className="sr-only"
        onChange={(event) => {
          emit([...(event.target.files ?? [])]);
          // Reset so selecting the same file twice fires change twice.
          event.target.value = '';
        }}
      />
      <input
        ref={folderInput}
        type="file"
        multiple
        // Non-standard but universally implemented, and the only way to offer
        // folder selection from a browse dialog.
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        className="sr-only"
        onChange={(event) => {
          emit([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
      <input
        ref={cameraInput}
        type="file"
        multiple
        accept="image/*"
        // `environment` asks for the rear camera, which is the one pointed at a
        // receipt on a van seat.
        capture="environment"
        className="sr-only"
        onChange={(event) => {
          emit([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * Walks a dropped directory.
 *
 * `readEntries` returns at most 100 entries per call and signals the end with
 * an empty batch, a subtlety that silently truncates large folders if missed,
 * which is exactly the case this feature exists for.
 */
async function collectEntry(entry: FileSystemEntry, into: File[], depth = 0): Promise<void> {
  if (depth > 8) return;

  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) => {
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
    });
    // Skip macOS metadata and hidden files; a user dragging a folder means the
    // documents in it, not .DS_Store.
    if (file !== null && !file.name.startsWith('.')) into.push(file);
    return;
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve) => {
        reader.readEntries(resolve, () => resolve([]));
      });
      if (batch.length === 0) break;
      for (const child of batch) {
        await collectEntry(child, into, depth + 1);
      }
    }
  }
}
